/**
 * RoleSelectionGate
 * Locked-down role selection. After login the server issues a BASE token
 * (`roleSelected: false`) that cannot reach any permission-gated endpoint. This
 * gate wraps the authenticated app:
 *   - roleSelected !== false  → render the app normally.
 *   - roleSelected === false  → force role selection first:
 *       • exactly 1 role  → auto-select (no screen),
 *       • more than 1     → show a "Select your role" screen,
 *       • 0 roles         → dead-end message (no access assigned).
 * On select → POST /users/me/select-role → persist the scoped token → reload so
 * permissions/menu re-resolve for the chosen role + unit.
 */
import { ReactNode, useEffect, useState } from 'react';
import { profileService, type MyRole } from '../../services/profile.service';
import { projectsService, type ProjectBranding } from '../../services/projects.service';

// Read the `roleSelected` claim from the JWT without any dependency.
function readRoleSelected(token: string | null): boolean | undefined {
  if (!token) return undefined;
  try {
    const seg = token.split('.')[1]?.replace(/-/g, '+').replace(/_/g, '/');
    if (!seg) return undefined;
    return JSON.parse(atob(seg))?.roleSelected;
  } catch {
    return undefined;
  }
}

interface Props {
  children: ReactNode;
}

const RoleSelectionGate = ({ children }: Props) => {
  const token = typeof window !== 'undefined' ? localStorage.getItem('accessToken') : null;
  const needsSelection = !!token && readRoleSelected(token) === false;

  const [roles, setRoles] = useState<MyRole[] | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  // The logged-in project's branding (from the token's appId) so the loader shows the
  // selected project's own logo, blinking, instead of a generic shield.
  const [branding, setBranding] = useState<ProjectBranding | null>(null);

  // Fetch the current project's logo for the loader animation (best-effort).
  useEffect(() => {
    if (!needsSelection) return;
    let mounted = true;
    projectsService
      .getCurrentBranding()
      .then((b) => { if (mounted) setBranding(b); })
      .catch(() => { /* fall back to the shield mark */ });
    return () => { mounted = false; };
  }, [needsSelection]);

  useEffect(() => {
    if (!needsSelection) return;
    let mounted = true;

    (async () => {
      try {
        const list = await profileService.getMyRoles();
        if (!mounted) return;
        if (list.length === 1) {
          await select(list[0].roleBindingId); // single role → auto-select, no screen
        } else {
          setRoles(list);
        }
      } catch {
        if (mounted) {
          setError('Could not load your roles. Please try again.');
          setRoles([]);
        }
      }
    })();

    return () => {
      mounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [needsSelection]);

  // Escape hatch: clear the session and return to the login screen. Needed on the
  // dead-end states (no role / load error) so a user isn't stranded on this gate.
  const backToLogin = () => {
    try {
      localStorage.removeItem('accessToken');
      localStorage.removeItem('activeRoleBindingId');
    } catch {
      /* ignore storage errors */
    }
    window.location.href = '/login';
  };

  const select = async (roleBindingId: string) => {
    if (busy) return;
    setBusy(true);
    setError('');
    try {
      const res = await profileService.selectRole(roleBindingId);
      if (res?.accessToken) {
        localStorage.setItem('accessToken', res.accessToken);
        localStorage.setItem('activeRoleBindingId', res.roleBindingId);
        // Re-bootstrap so permissions + menu resolve for the selected role/unit.
        window.location.reload();
      } else {
        setBusy(false);
        setError('Could not select role. Please try again.');
      }
    } catch {
      setBusy(false);
      setError('Could not select role. Please try again.');
    }
  };

  if (!needsSelection) return <>{children}</>;

  // ---- full-page selection UI ----
  const loading = roles === null || busy;

  return (
    <div className="min-h-screen flex items-center justify-center bg-canvas dark:bg-gray-900 p-4">
      <div className="ds-card w-full max-w-md p-6">
        {!loading && (
          <div className="text-center mb-5">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-600 to-indigo-600 text-white flex items-center justify-center mx-auto mb-3">
              <i className="pi pi-shield" style={{ fontSize: '1.4rem' }} />
            </div>
            <h1 className="text-lg font-semibold text-ink">Select your role</h1>
            <p className="text-sm text-ink-muted mt-1">
              You have more than one role. Pick the one to continue with.
            </p>
          </div>
        )}

        {error && (
          <div className="mb-4 text-sm text-rose-600 dark:text-rose-400 bg-rose-50 dark:bg-rose-900/20 border border-rose-200 dark:border-rose-800 rounded-lg px-3 py-2">
            {error}
          </div>
        )}

        {loading ? (
          <div className="flex flex-col items-center py-10 gap-6 text-center">
            {/* The selected project's logo, blinking over a soft pulsing glow (falls back
                to the brand shield when the project has no logo). */}
            <div className="relative flex items-center justify-center">
              <span className="absolute w-24 h-24 rounded-full bg-blue-400/20 blur-2xl animate-pulse" />
              {branding?.logoUrl ? (
                <img
                  src={branding.logoUrl}
                  alt={branding.name || 'Project'}
                  className="relative w-20 h-20 object-contain rounded-2xl animate-pulse"
                />
              ) : (
                <div className="relative w-16 h-16 rounded-2xl bg-gradient-to-br from-blue-600 to-indigo-600 text-white flex items-center justify-center shadow-lg animate-pulse">
                  <i className="pi pi-shield" style={{ fontSize: '1.8rem' }} />
                </div>
              )}
            </div>
            <span className="text-sm text-ink-muted flex items-center gap-2">
              <i className="pi pi-spin pi-spinner text-blue-600" />
              {busy ? 'Signing you in…' : 'Loading your roles…'}
            </span>
          </div>
        ) : roles && roles.length === 0 ? (
          <div className="text-center py-6">
            <p className="text-sm text-gray-600 dark:text-gray-300">
              No active role is assigned to your account. Please contact your administrator.
            </p>
            <button
              type="button"
              onClick={backToLogin}
              data-testid="RoleSelect.BackToLogin"
              className="mt-4 inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 text-sm font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
            >
              <i className="pi pi-arrow-left" style={{ fontSize: '0.8rem' }} />
              Back to login
            </button>
          </div>
        ) : (
          <div className="flex flex-col gap-2">
            {roles?.map((r) => (
              <button
                key={r.roleBindingId}
                type="button"
                onClick={() => select(r.roleBindingId)}
                disabled={busy}
                data-testid={`RoleSelect.Option.${r.roleBindingId}`}
                className="flex items-center justify-between text-left px-4 py-3 rounded-xl border border-line hover:border-blue-500 hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors disabled:opacity-50"
              >
                <span className="flex flex-col">
                  <span className="font-medium text-ink">
                    {r.roleName || r.roleKey || 'Role'}
                  </span>
                  <span className="text-xs text-ink-muted">
                    <i className="pi pi-building mr-1" style={{ fontSize: '0.7rem' }} />
                    {r.isGlobal ? 'All units (global)' : r.unitName || 'Unit'}
                  </span>
                </span>
                <i className="pi pi-angle-right text-gray-400" />
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default RoleSelectionGate;
